import * as React from 'react'
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react'
import { TooltipOptions } from '@shared/ui/Overlays/Tooltip/types/type'
import { TooltipContext } from '@shared/ui/Overlays/Tooltip/context'

export function useTooltip({
  initialOpen = false,
  placement = 'top',
  closingDelay = 0,
  useEvent = 'hover',
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  floatingMiddleware = [],
}: TooltipOptions = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(initialOpen)

  const open = controlledOpen ?? uncontrolledOpen
  const setOpen = setControlledOpen ?? setUncontrolledOpen

  let timeout: ReturnType<typeof setTimeout> | null = null

  const handleOpenChange = (open: boolean) => {
    if (open) {
      clearTimeout(timeout)
      setOpen(open)
    } else
      timeout = setTimeout(() => {
        setOpen(open)
      }, closingDelay)
  }

  const data = useFloating({
    placement,
    open,
    onOpenChange: handleOpenChange,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({
        crossAxis: placement.includes('-'),
        fallbackAxisSideDirection: 'start',
        padding: 5,
      }),
      shift({ padding: 5 }),
      ...floatingMiddleware,
    ],
  })

  const context = data.context

  const click = useClick(context, {
    enabled: controlledOpen == null && useEvent === 'click',
  })

  const hover = useHover(context, {
    move: false,
    enabled: controlledOpen == null && useEvent === 'hover',
  })
  const focus = useFocus(context, {
    enabled: controlledOpen == null && useEvent === 'hover',
  })
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'tooltip' })

  const interactions = useInteractions([click, role, hover, focus, dismiss])

  return React.useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...data,
    }),
    [open, setOpen, interactions, data],
  )
}

export const useTooltipContext = () => {
  const context = React.useContext(TooltipContext)

  if (context == null) {
    throw new Error('Tooltip components must be wrapped in <Tooltip />')
  }

  return context
}
